import React, { useEffect, useMemo, useRef } from 'react';
import QRCodeStyling from 'qr-code-styling';
import * as S from './StyledQRCode.styles';

const LOGO_PATH = '/media/favicon.png';
const DOTS_COLOR = '#01AB7D';
const BACKGROUND_COLOR = '#ffffff';

const buildQRCodeOptions = ({
  value,
  size,
  logoSize,
  logoMargin,
  errorCorrectionLevel,
}) => ({
  width: size,
  height: size,
  type: 'svg',
  data: value,
  image: LOGO_PATH,
  dotsOptions: {
    color: DOTS_COLOR,
    type: 'rounded',
  },
  backgroundOptions: {
    color: BACKGROUND_COLOR,
  },
  imageOptions: {
    crossOrigin: 'anonymous',
    margin: logoMargin,
    imageSize: logoSize,
    hideBackgroundDots: true,
  },
  cornersSquareOptions: {
    color: DOTS_COLOR,
    type: 'extra-rounded',
  },
  cornersDotOptions: {
    color: DOTS_COLOR,
    type: 'dot',
  },
  qrOptions: {
    typeNumber: 0,
    mode: 'Byte',
    errorCorrectionLevel,
  },
});

const QRCodeCanvas = ({ options, bordered, ariaLabel, className }) => {
  const containerRef = useRef(null);
  const qrCodeRef = useRef(null);

  useEffect(() => {
    if (!qrCodeRef.current) {
      qrCodeRef.current = new QRCodeStyling(options);
      qrCodeRef.current.append(containerRef.current);
      return;
    }

    qrCodeRef.current.update(options);
  }, [options]);

  return (
    <S.Frame
      ref={containerRef}
      $bordered={bordered}
      className={className}
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
    />
  );
};

/**
 * QR Code estilizado com a marca da Jusfy, reutilizável em qualquer tela.
 *
 * @param {string} value - Conteúdo que será codificado (não o base64 de uma imagem)
 * @param {number} size - Largura/altura em px
 * @param {number} logoSize - Proporção do QR ocupada pela logo, de 0 a 1
 * @param {number} logoMargin - Espaço em branco em volta da logo, em px
 * @param {string} errorCorrectionLevel - Nível de correção de erro do QR ('L' | 'M' | 'Q' | 'H')
 * @param {boolean} bordered - Exibe borda e padding em volta do QR Code
 * @param {string} ariaLabel - Rótulo acessível; quando omitido o QR é tratado como decorativo
 * @param {string} placeholder - Texto exibido enquanto não há `value`
 */
const StyledQRCode = ({
  value,
  size = 220,
  logoSize = 0.3,
  logoMargin = 8,
  errorCorrectionLevel = 'M',
  bordered = true,
  ariaLabel,
  placeholder = 'Aguardando código...',
  className,
}) => {
  const options = useMemo(
    () =>
      buildQRCodeOptions({
        value,
        size,
        logoSize,
        logoMargin,
        errorCorrectionLevel,
      }),
    [value, size, logoSize, logoMargin, errorCorrectionLevel]
  );

  if (!value) {
    return (
      <S.Placeholder $size={size} className={className}>
        {placeholder}
      </S.Placeholder>
    );
  }

  return (
    <QRCodeCanvas
      options={options}
      bordered={bordered}
      ariaLabel={ariaLabel}
      className={className}
    />
  );
};

export default StyledQRCode;
